import {Component, ViewChild} from '@angular/core';
import {IBaseModalComponent} from '../../../../../sdk/src/components/ibase-modal/ibase-modal.component';
import {PrimeNgImportsModule} from '../../../../../sdk/src/prime-ng-imports';
import {firstValueFrom} from 'rxjs';
import {CpmCTaskExecutionApiService} from '../../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-task-execution.api-service';
import {DynamicDialogConfig} from 'primeng/dynamicdialog';
import {GenericTableComponent} from '../../../../../sdk/src/components/generic-table/generic-table.component';
import {
    ETableColDataType,
    ETableColFilterType,
    TableColDef
} from '../../../../../sdk/src/components/generic-table/table-col-def';
import {CpmCJobLogEntryProxyApiModel} from '../../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-job-log-entry-proxy.api-model';
import {TaskExecutionUtils} from '../../../utils/task-execution.utils';
import {DateUtils} from '../../../../../sdk/src/utils/date.utils';
import {SDKConfig} from '../../../../../sdk/src/sdk.config';

export class TaskExecutionLogsModalConfig {
    constructor(
        public taskUUID: string,
        public jobUUID: string,
        public jobCreatedAt: Date | undefined,
        public taskName: string | undefined
    ) {}
}

@Component({
    selector: 'app-task-execution-log-modal',
    templateUrl: './task-execution-log-modal.component.html',
    imports: [PrimeNgImportsModule, GenericTableComponent]
})
export class TaskExecutionLogsModalComponent extends IBaseModalComponent<
    TaskExecutionLogsModalComponent,
    TaskExecutionLogsModalConfig
> {
    protected readonly CpmCTaskExecutionApiService = CpmCTaskExecutionApiService;

    // ===============================================================
    // ===============================================================

    protected _contentMinHeight = '';
    protected _taskUUID!: string;
    protected _jobUUID!: string;
    protected _tableCols: TableColDef[] = [];
    protected _items: CpmCJobLogEntryProxyApiModel[] = [];
    protected _totalItemCount = -1;

    @ViewChild(GenericTableComponent, {static: true})
    protected genericTableComponent!: GenericTableComponent<CpmCJobLogEntryProxyApiModel>;

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.config.width = '75%';
        this._contentMinHeight = '25rem';

        this.config.draggable = true;
        this.config.maximizable = true;
        this.config.resizable = true;
        this.config.keepInViewport = true;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Get dialog service parameters
     * @param config modal configuration
     * @returns DynamicDialogConfig<TaskDependsWarningModalConfig>
     */
    public static getDialogServiceParameters(
        config: TaskExecutionLogsModalConfig
    ): DynamicDialogConfig<TaskExecutionLogsModalConfig> {
        return {
            modal: true,
            header: `Execution ${DateUtils.format(config.jobCreatedAt, SDKConfig.dateFormatFull)} logs for task ${config.taskName}`,
            data: config
        };
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    protected override async onReady(): Promise<void> {
        this._taskUUID = this.config.data!.taskUUID;
        this._jobUUID = this.config.data!.jobUUID;
        this.showLoading = true;

        this.loadingStr = 'Initializing...';

        await this.asyncCritical(async () => {
            this.initColumns();
            await this.refresh();
        });

        this.showLoading = false;
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * Refresh
     */
    public async refresh(): Promise<void> {
        this.showLoading = true;
        this.showError = false;
        this.loadingStr = 'Loading...';

        await this.asyncCritical(async () => {
            const result = await firstValueFrom(
                CpmCTaskExecutionApiService.getTaskExecutionLogList(
                    this._taskUUID,
                    this._jobUUID,
                    {},
                    this.genericTableComponent.paginatorConfig
                )
            );

            this._items = result.data;
            this._totalItemCount = result.total;
        });

        this.showLoading = false;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Init the columns (Error handling is done in the parent)
     */
    protected initColumns() {
        this._tableCols = [
            {
                field: 'time',
                header: 'Time',
                dataType: ETableColDataType.DATE,
                customDateFormat: SDKConfig.dateFormatFullMs,
                tdStyle: {'vertical-align': 'baseline'}
            },
            {
                field: 'level',
                header: 'Level',
                dataType: ETableColDataType.TAGS,
                tags: TaskExecutionUtils.jobLogEntryLevelTags,
                filterable: true,
                filterType: ETableColFilterType.SELECT,
                filterSelectOptions: TaskExecutionUtils.jobLogEntryLevelTags,
                tdStyle: {'vertical-align': 'baseline'}
            },
            {
                field: 'message',
                header: 'Message',
                dataType: ETableColDataType.TEXT,
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.TEXT,
                tdStyle: {
                    'vertical-align': 'baseline',
                    'font-family': 'monospace, monospace',
                    'word-wrap': 'break-word',
                    'overflow-wrap': 'break-word',
                    'white-space': 'normal',
                    width: '80%'
                }
            }
        ];
    }
}
